import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import * as api from '../lib/api';
import {
  Tournament, Team, Player, MatchUp, Game, HoleScore,
  MatchUpResult, PlayerStats, TeamStanding,
} from '../data/types';
import { VINOR_HOLES } from '../data/course';
import {
  calculateMatchUpResult,
  calculateAllPlayerStats,
  calculateFinalStandings,
  propagateBracketResults,
  calculateGameResult,
} from '../engine/scoring';
// Polling interval in milliseconds (10 seconds)
const POLL_INTERVAL_MS = 10_000;

interface TournamentContextType {
  // Data
  tournament: Tournament | null;
  teams: Team[];
  players: Player[];
  matchUps: MatchUp[];
  games: Game[];
  holeScores: HoleScore[];
  // Computed
  matchUpResults: Map<string, MatchUpResult>;
  playerStats: PlayerStats[];
  standings: TeamStanding[];
  // Loading
  isLoading: boolean;
  // Actions
  refreshAll: () => Promise<void>;
  upsertScore: (gameId: string, holeNumber: number, teamSide: 'a' | 'b', strokes: number) => Promise<void>;
  deleteScore: (gameId: string, holeNumber: number, teamSide: 'a' | 'b') => Promise<void>;
  // Helpers
  getTeam: (id: string) => Team | undefined;
  getPlayer: (id: string) => Player | undefined;
  getMatchUp: (id: string) => MatchUp | undefined;
  getGame: (id: string) => Game | undefined;
  getGamesByMatchUp: (matchUpId: string) => Game[];
  getScoresByGame: (gameId: string) => HoleScore[];
  getPlayersByTeam: (teamId: string) => Player[];
}

const TournamentContext = createContext<TournamentContextType | null>(null);

export function TournamentProvider({ children }: { children: React.ReactNode }) {
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [matchUps, setMatchUps] = useState<MatchUp[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [holeScores, setHoleScores] = useState<HoleScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Computed values
  const [matchUpResults, setMatchUpResults] = useState<Map<string, MatchUpResult>>(new Map());
  const [playerStats, setPlayerStats] = useState<PlayerStats[]>([]);
  const [standings, setStandings] = useState<TeamStanding[]>([]);

  // Recalculate derived data whenever core data changes
  const recalculate = useCallback((
    currentMatchUps: MatchUp[],
    currentGames: Game[],
    currentHoleScores: HoleScore[],
    currentTeams: Team[],
    currentPlayers: Player[]
  ) => {
    // Calculate match-up results
    const resultsMap = new Map<string, MatchUpResult>();
    for (const mu of currentMatchUps) {
      const result = calculateMatchUpResult(mu, currentGames, currentHoleScores);
      resultsMap.set(mu.id, result);
    }
    setMatchUpResults(resultsMap);

    // Calculate player statistics
    const stats = calculateAllPlayerStats(
      currentPlayers,
      currentTeams,
      currentGames,
      currentHoleScores,
      VINOR_HOLES
    );
    setPlayerStats(stats);

    // Calculate standings
    const results = Array.from(resultsMap.values());
    const currentStandings = calculateFinalStandings(currentMatchUps, results, currentTeams);
    setStandings(currentStandings);

    // Check for bracket propagation
    const propagations = propagateBracketResults(currentMatchUps, results);
    if (propagations.length > 0) {
      // Auto-propagate in background (don't await)
      api.propagateBracket(propagations).then(success => {
        if (success) {
          console.log('Bracket auto-propagated:', propagations);
        }
      });
    }
  }, []);

  // Initial data load
  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const t = await api.fetchTournament();
      if (!t) {
        setIsLoading(false);
        return;
      }
      setTournament(t);

      const [fetchedTeams, fetchedPlayers, fetchedMatchUps] = await Promise.all([
        api.fetchTeams(t.id),
        api.fetchPlayers(t.id),
        api.fetchMatchUps(t.id),
      ]);

      setTeams(fetchedTeams);
      setPlayers(fetchedPlayers);
      setMatchUps(fetchedMatchUps);

      const matchUpIds = fetchedMatchUps.map(m => m.id);
      const fetchedGames = await api.fetchGames(matchUpIds);
      setGames(fetchedGames);

      const gameIds = fetchedGames.map(g => g.id);
      const fetchedScores = await api.fetchHoleScores(gameIds);
      setHoleScores(fetchedScores);

      recalculate(fetchedMatchUps, fetchedGames, fetchedScores, fetchedTeams, fetchedPlayers);
    } catch (err) {
      console.error('refreshAll error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [recalculate]);

  // Optimistic score upsert
  const upsertScore = useCallback(async (
    gameId: string,
    holeNumber: number,
    teamSide: 'a' | 'b',
    strokes: number
  ) => {
    // Optimistic update
    setHoleScores(prev => {
      const existing = prev.findIndex(
        hs => hs.game_id === gameId && hs.hole_number === holeNumber && hs.team_side === teamSide
      );
      const newScore: HoleScore = {
        id: `temp-${gameId}-${holeNumber}-${teamSide}`,
        game_id: gameId,
        hole_number: holeNumber,
        team_side: teamSide,
        strokes,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], strokes, updated_at: new Date().toISOString() };
        return updated;
      }
      return [...prev, newScore];
    });

    // Persist to database
    await api.upsertHoleScore(gameId, holeNumber, teamSide, strokes);

    // Check if game is decided after this score
    const currentScores = holeScores.filter(hs => hs.game_id === gameId);
    // Add/update the new score in the local copy
    const updatedScores = [...currentScores.filter(
      hs => !(hs.hole_number === holeNumber && hs.team_side === teamSide)
    ), { id: 'temp', game_id: gameId, hole_number: holeNumber, team_side: teamSide, strokes, created_at: '', updated_at: '' }];

    const game = games.find(g => g.id === gameId);
    if (game) {
      const result = calculateGameResult(game, updatedScores);
      if (result.matchPlayState.isFinished && game.status !== 'completed') {
        await api.updateGame(gameId, {
          status: 'completed',
          winner_side: result.winnerSide,
          result_text: result.matchPlayState.resultText,
          holes_played: result.matchPlayState.holesPlayed,
        });
      } else if (!result.matchPlayState.isFinished && game.status === 'not_started') {
        await api.updateGame(gameId, { status: 'in_progress' });
      }
    }
  }, [games, holeScores]);

  const deleteScore = useCallback(async (
    gameId: string,
    holeNumber: number,
    teamSide: 'a' | 'b'
  ) => {
    // Optimistic remove
    setHoleScores(prev =>
      prev.filter(hs =>
        !(hs.game_id === gameId && hs.hole_number === holeNumber && hs.team_side === teamSide)
      )
    );
    await api.deleteHoleScore(gameId, holeNumber, teamSide);
  }, []);

  // Silent refresh – fetches latest data without triggering loading spinner
  const silentRefresh = useCallback(async () => {
    if (!tournament) return;
    try {
      const [freshTeams, freshPlayers, freshMatchUps] = await Promise.all([
        api.fetchTeams(tournament.id),
        api.fetchPlayers(tournament.id),
        api.fetchMatchUps(tournament.id),
      ]);

      setTeams(freshTeams);
      setPlayers(freshPlayers);
      setMatchUps(freshMatchUps);

      const matchUpIds = freshMatchUps.map(m => m.id);
      const freshGames = await api.fetchGames(matchUpIds);
      setGames(freshGames);

      const gameIds = freshGames.map(g => g.id);
      const freshScores = await api.fetchHoleScores(gameIds);
      setHoleScores(freshScores);
    } catch (err) {
      console.error('Silent refresh error:', err);
    }
  }, [tournament]);

  // Polling: refresh data every POLL_INTERVAL_MS instead of using Realtime
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!tournament) return;

    pollingRef.current = setInterval(silentRefresh, POLL_INTERVAL_MS);
    console.log(`[Polling] Started – refreshing every ${POLL_INTERVAL_MS / 1000}s`);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        console.log('[Polling] Stopped');
      }
    };
  }, [tournament, silentRefresh]);

  // Recalculate when data changes
  useEffect(() => {
    if (matchUps.length > 0) {
      recalculate(matchUps, games, holeScores, teams, players);
    }
  }, [matchUps, games, holeScores, teams, players, recalculate]);

  // Initial load
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Helper functions
  const getTeam = useCallback((id: string) => teams.find(t => t.id === id), [teams]);
  const getPlayer = useCallback((id: string) => players.find(p => p.id === id), [players]);
  const getMatchUp = useCallback((id: string) => matchUps.find(m => m.id === id), [matchUps]);
  const getGame = useCallback((id: string) => games.find(g => g.id === id), [games]);
  const getGamesByMatchUp = useCallback((matchUpId: string) =>
    games.filter(g => g.match_up_id === matchUpId).sort((a, b) => a.game_number - b.game_number),
  [games]);
  const getScoresByGame = useCallback((gameId: string) =>
    holeScores.filter(hs => hs.game_id === gameId),
  [holeScores]);
  const getPlayersByTeam = useCallback((teamId: string) =>
    players.filter(p => p.team_id === teamId),
  [players]);

  const value: TournamentContextType = {
    tournament,
    teams,
    players,
    matchUps,
    games,
    holeScores,
    matchUpResults,
    playerStats,
    standings,
    isLoading,
    refreshAll,
    upsertScore,
    deleteScore,
    getTeam,
    getPlayer,
    getMatchUp,
    getGame,
    getGamesByMatchUp,
    getScoresByGame,
    getPlayersByTeam,
  };

  return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
}

export function useTournament(): TournamentContextType {
  const ctx = useContext(TournamentContext);
  if (!ctx) throw new Error('useTournament must be used within TournamentProvider');
  return ctx;
}
